// frontend/src/components/scenariolist.jsx

import { useState, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';                // ✅ New: SweetAlert for delete
import { toast } from 'react-toastify';
import { Oval } from 'react-loader-spinner';
import 'react-toastify/dist/ReactToastify.css';

export default function ScenarioList() {
  const [clientId, setClientId] = useState('');
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editExpectedStatus, setEditExpectedStatus] = useState('');

  const clientIdRef = useRef();

  const fetchScenarios = async () => {
    if (!clientId.trim()) {
      toast.error('Please enter Client ID.');
      clientIdRef.current?.focus();
      return;
    }
    try {
      setLoading(true);
      const response = await axios.get(`/api/scenarios?clientId=${clientId}`);
      setScenarios(response.data.scenarios);
      toast.success('Scenarios fetched successfully!');
    } catch (error) {
      console.error('Fetch Error', error);
      toast.error('Failed to fetch scenarios.');
    } finally {
      setLoading(false);
    }
  };

  const toggleApprove = async (id, approved) => {
    try {
      setLoading(true);
      await axios.patch(`/api/scenarios/${id}/approve`, { clientId, approved: !approved });
      toast.success(`Scenario ${!approved ? 'approved' : 'unapproved'}!`);
      fetchScenarios();
    } catch (error) {
      console.error('Approve Error', error);
      toast.error('Failed to update approval.');
    } finally {
      setLoading(false);
    }
  };

  const deleteScenario = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Scenario?',
      text: 'This will be permanently deleted!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, cancel'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await axios.delete(`/api/scenarios/${id}`, { data: { clientId } });
        toast.success('Scenario deleted!');
        fetchScenarios();
      } catch (error) {
        console.error('Delete Error', error);
        toast.error('Failed to delete scenario.');
      } finally {
        setLoading(false);
      }
    }
  };

  const startEditing = (scenario) => {
    setEditingId(scenario._id);
    setEditName(scenario.name);
    setEditExpectedStatus(scenario.expectedStatus);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditName('');
    setEditExpectedStatus('');
  };

  const saveEdit = async (id) => {
    if (!editName.trim() || !editExpectedStatus) {
      toast.error('Name and Expected Status are required.');
      return;
    }
    try {
      setLoading(true);
      await axios.put(`/api/scenarios/${id}`, {
        clientId,
        name: editName,
        expectedStatus: editExpectedStatus,
      });
      toast.success('Scenario updated successfully!');
      cancelEditing();
      fetchScenarios();
    } catch (error) {
      console.error('Save Edit Error', error);
      toast.error('Failed to update scenario.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <h2 style={{ fontSize: '30px', marginBottom: '20px', color: '#2c3e50' }}>📋 Scenario List</h2>

      <div style={{ marginBottom: 24, background: '#fff', padding: 24, borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }}>
        <h3>🔍 Fetch Client Scenarios</h3>

        <div style={{ marginBottom: 12 }}>
          <input
            ref={clientIdRef}
            type="text"
            placeholder="Enter Client ID"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '16px',
              marginBottom: 8,
              borderRadius: '8px',
              border: '1px solid #ccc'
            }}
          />
          <button
            onClick={fetchScenarios}
            disabled={loading || !clientId}
            style={{
              padding: '12px 24px',
              backgroundColor: '#3498db',
              color: 'white',
              fontWeight: 'bold',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            🚀 Fetch
          </button>
        </div>
      </div>

      <div style={{ background: '#fff', padding: 24, borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }}>
        <h3>📝 Existing Scenarios {loading && '(Loading...)'}</h3>

        {loading ? (
          <div style={{ margin: '20px 0', textAlign: 'center' }}>
            <Oval height={40} width={40} color="#4fa94d" visible={true} ariaLabel="oval-loading" />
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f7f7f7' }}>
                <th>Method</th>
                <th>Path</th>
                <th>Name</th>
                <th>Expected Status</th>
                <th>Approved</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {scenarios.map((scenario, idx) => (
                <tr key={scenario._id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                  <td>{scenario.method}</td>
                  <td>{scenario.path}</td>
                  <td>
                    {editingId === scenario._id ? (
                      <input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Scenario Name"
                        style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                      />
                    ) : (
                      scenario.name
                    )}
                  </td>
                  <td>
                    {editingId === scenario._id ? (
                      <input
                        value={editExpectedStatus}
                        onChange={(e) => setEditExpectedStatus(e.target.value)}
                        type="number"
                        placeholder="Expected Status"
                        style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                      />
                    ) : (
                      scenario.expectedStatus
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {scenario.approved ? '✅' : '❌'}
                  </td>
                  <td>
                    {editingId === scenario._id ? (
                      <>
                        <button
                          onClick={() => saveEdit(scenario._id)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            fontWeight: 'bold',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            marginRight: '8px'
                          }}
                        >
                          Save
                        </button>
                        <button
                          onClick={cancelEditing}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            fontWeight: 'bold',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => toggleApprove(scenario._id, scenario.approved)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: scenario.approved ? '#f39c12' : '#2ecc71',
                            color: 'white',
                            fontWeight: 'bold',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            marginRight: '8px'
                          }}
                        >
                          {scenario.approved ? 'Unapprove' : 'Approve'}
                        </button>
                        <button
                          onClick={() => deleteScenario(scenario._id)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#e74c3c',
                            color: 'white',
                            fontWeight: 'bold',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            marginRight: '8px'
                          }}
                        >
                          Delete
                        </button>
                        <button
                          onClick={() => startEditing(scenario)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#3498db',
                            color: 'white',
                            fontWeight: 'bold',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                        >
                          Edit
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
